/**
 * Jetton (TEP-74) helpers. TONado v1.1 ships pool_jetton.tolk; the helpers
 * here let clients construct deposit messages and look up the pool's jetton
 * wallet address.
 */
import { beginCell, toNano } from "@ton/core";
import { serialiseGroth16Proof } from "./pool";
/** TEP-74 op codes. */
export const JETTON_OPS = {
    TRANSFER: 0xf8a7ea5,
    TRANSFER_NOTIFICATION: 0x7362d09c,
    INTERNAL_TRANSFER: 0x178d4519,
    EXCESSES: 0xd53276db,
    BURN: 0x595f07bc,
};
/**
 * Derive the jetton wallet address for `owner` against the master `jettonMaster`.
 * Calls the master's `get_wallet_address` get-method.
 */
export async function getJettonWalletAddress(client, jettonMaster, owner) {
    const ownerCell = beginCell().storeAddress(owner).endCell();
    const result = await client.runMethod(jettonMaster, "get_wallet_address", [
        { type: "slice", cell: ownerCell },
    ]);
    return result.stack.readAddress();
}
/**
 * Build a jetton transfer message body (TEP-74 op 0xf8a7ea5). Used by the CLI
 * to deposit jettons into a TONado pool. The `forwardPayload` is the
 * commitment, which the pool reads from the `transfer_notification` body.
 */
export function buildJettonTransferBody(opts) {
    return beginCell()
        .storeUint(JETTON_OPS.TRANSFER, 32)
        .storeUint(opts.queryId ?? 0n, 64)
        .storeCoins(opts.amount)
        .storeAddress(opts.destination)
        .storeAddress(opts.responseDestination)
        .storeBit(false) // no custom payload
        .storeCoins(opts.forwardTonAmount)
        .storeBit(true) // forward_payload ref
        .storeRef(opts.forwardPayload)
        .endCell();
}
/**
 * Encode the full TONado deposit forward_payload — what the depositor
 * packs into the TEP-74 transfer's `forward_payload` ref. Mirrors the
 * `DepositJettonPayload` struct in pool_jetton.tolk byte-for-byte.
 *
 * Layout (single cell, 800 inline bits + 1 ref):
 *   uint256 commitment
 *   uint256 prevRoot
 *   uint256 newRoot
 *   uint32  leafIndex
 *   ref     mergeProof   → Cell<MergeProof> (3 sub-refs piA, piB, piC)
 *
 * `mergeProof` MUST be the already-serialized merge-Groth16 proof cell
 * produced by `pool.ts :: serialiseGroth16Proof` so the on-chain
 * verifier accepts it.
 */
export function buildJettonDepositForwardPayload(opts) {
    return beginCell()
        .storeUint(opts.commitment, 256)
        .storeUint(opts.prevRoot, 256)
        .storeUint(opts.newRoot, 256)
        .storeUint(opts.leafIndex, 32)
        .storeRef(opts.mergeProof)
        .endCell();
}
/**
 * Recommended forward-TON included in a jetton deposit. The pool's
 * MIN_DEPOSIT_GAS floor is 0.20 TON; we default a bit higher for
 * cushion. The depositor sends this to THEIR OWN jetton wallet as
 * `forward_ton_amount`; it flows through to the pool's jetton wallet,
 * then to the pool itself, where excess (above REFUND_RESERVE) is
 * refunded back to the depositor.
 */
export const RECOMMENDED_JETTON_FORWARD_TON = toNano("0.25");
/**
 * High-level helper for the jetton deposit flow. Returns BOTH:
 *   - the depositor's *own jetton wallet address* (the message
 *     destination)
 *   - the TEP-74 transfer body to send there
 *
 * Layered call:
 *   transfer → depositor's jetton wallet
 *     → (TEP-74 internal_transfer) → pool's jetton wallet
 *       → (TEP-74 transfer_notification, with forward_payload) → pool
 *
 * The pool's `senderAddress` check on the notification will pass because
 * the chain of jetton-wallet transfers preserves the `pool` destination
 * and the pool's own wallet is the one ultimately calling
 * `transfer_notification`.
 */
export async function buildJettonDepositMessage(opts) {
    let depositorJettonWallet;
    if (opts.depositorJettonWallet) {
        depositorJettonWallet = opts.depositorJettonWallet;
    }
    else if (opts.client) {
        depositorJettonWallet = await getJettonWalletAddress(opts.client, opts.jettonMaster, opts.depositor);
    }
    else {
        throw new Error("buildJettonDepositMessage: pass either `client` or `depositorJettonWallet`");
    }
    const mergeProofCell = await serialiseGroth16Proof(opts.mergeProof);
    const forwardPayload = buildJettonDepositForwardPayload({
        commitment: opts.commitment,
        prevRoot: opts.prevRoot,
        newRoot: opts.newRoot,
        leafIndex: opts.leafIndex,
        mergeProof: mergeProofCell,
    });
    const forwardTonAmount = opts.forwardTonAmount ?? RECOMMENDED_JETTON_FORWARD_TON;
    const body = buildJettonTransferBody({
        queryId: opts.queryId ?? 0n,
        amount: opts.jettonAmount,
        destination: opts.poolAddress,
        // responseDestination is who receives leftover TON from the
        // depositor's wallet (separate from the pool's REFUND_RESERVE
        // refund). Send back to the depositor.
        responseDestination: opts.depositor,
        forwardTonAmount,
        forwardPayload,
    });
    return { destination: depositorJettonWallet, body, forwardTonAmount };
}
//# sourceMappingURL=jettons.js.map